import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useWallet } from "../contexts/WalletContext"
import Card from "../components/Card"
import Button from "../components/Button"
import Input from "../components/Input"
import { Phone, ArrowRight } from "lucide-react"

const AirtimeToCash = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    network: "",
    phoneNumber: "",
    amount: "",
  })

  const networks = [
    { id: "mtn", name: "MTN", rate: 0.7 },
    { id: "airtel", name: "Airtel", rate: 0.75 },
    { id: "glo", name: "Glo", rate: 0.7 },
    { id: "9mobile", name: "9mobile", rate: 0.7 },
  ]

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const calculateCashAmount = () => {
    const selectedNetwork = networks.find((n) => n.id === formData.network)
    if (!selectedNetwork || !formData.amount) return 0
    return (parseFloat(formData.amount) * selectedNetwork.rate).toFixed(2)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Here you would implement the actual airtime to cash conversion logic
      // This might involve calling your backend API
      
      // For now, we'll just show a success message
      alert("Airtime to cash conversion request submitted successfully!")
      navigate("/dashboard")
    } catch (error) {
      alert("Failed to process airtime to cash conversion. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    navigate("/login")
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Airtime to Cash</h1>
          <p className="text-gray-600 mt-2">Convert your airtime to cash instantly</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Network
              </label>
              <select
                name="network"
                value={formData.network}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a network</option>
                {networks.map((network) => (
                  <option key={network.id} value={network.id}>
                    {network.name} (Rate: {network.rate * 100}%)
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Phone Number"
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="Enter phone number"
              required
              icon={<Phone className="h-5 w-5 text-gray-400" />}
            />

            <Input
              label="Airtime Amount"
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Enter amount"
              required
              min="100"
              icon={<ArrowRight className="h-5 w-5 text-gray-400" />}
            />

            {formData.amount && formData.network && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600">
                  You will receive: ₦{calculateCashAmount()}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Rate: {networks.find((n) => n.id === formData.network)?.rate * 100}% of airtime value
                </p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Minimum amount: ₦100
              </p>
              <Button
                type="submit"
                disabled={loading}
              >
                {loading ? "Processing..." : "Convert Airtime"}
              </Button>
            </div>
          </form>
        </Card>

        <Card className="mt-8">
          <h2 className="text-xl font-semibold mb-4">How it works</h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 text-sm">1</span>
              </div>
              <p className="text-gray-600">Select your network provider and enter the amount you want to convert</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 text-sm">2</span>
              </div>
              <p className="text-gray-600">Transfer the airtime to our designated number (you'll receive this after submission)</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 text-sm">3</span>
              </div>
              <p className="text-gray-600">Once confirmed, we'll credit your wallet with the equivalent cash amount</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default AirtimeToCash 